import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreatePlanPayload,
  type Plan,
  PlanStatus,
  type UpdatePlanPayload,
} from './interfaces';
import { falconClient } from '@/data/@clients';
import { clientKeys } from '@/data/@clients/client-keys';
import { baseUrl } from '@/data/idp/base';

export const usePlans = () => {
  const result = useQuery<{ plans: Plan[] }>({
    queryKey: [clientKeys.usePlansIndex],
    queryFn: () => falconClient.get(`${baseUrl}/plans`),
  });

  const respPlans = result.data?.plans || [];
  const plansCount = respPlans.length || 0;

  const inProgressPlans = respPlans.find(
    ({ status }) => status === PlanStatus.InProgress
  );
  const draftPlans = respPlans.filter(
    ({ status }) => status === PlanStatus.Draft
  );
  const closedPlans = respPlans.filter(
    ({ status }) => status === PlanStatus.Closed
  );

  const plans = {
    [PlanStatus.Draft]: draftPlans,
    [PlanStatus.InProgress]: inProgressPlans,
    [PlanStatus.Closed]: closedPlans,
  };

  return { plans, plansCount, ...result };
};

export const usePlanShow = (id: string) => {
  const response = useQuery<Plan>({
    queryKey: [clientKeys.usePlanShow, id],
    queryFn: () => falconClient.get(`${baseUrl}/plans/${id}`),
    enabled: !!id,
  });

  const plan = response?.data || null;

  return { plan, ...response };
};

export const useCreatePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePlanPayload): Promise<Plan> =>
      falconClient.post(`${baseUrl}/plans`, payload),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: [clientKeys.usePlansIndex],
      });
    },
  });
};

export const useUpdatePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      title,
      description,
      duration_in_months,
      feedback_ids,
    }: UpdatePlanPayload): Promise<Plan> =>
      falconClient.patch(`${baseUrl}/plans/${id}`, {
        title,
        description,
        duration_in_months,
        feedback_ids,
      }),
    onSuccess: (updatedPlan) => {
      queryClient.resetQueries({
        queryKey: [clientKeys.usePlansIndex],
      });
      queryClient.setQueryData(
        [clientKeys.usePlanShow, updatedPlan.id],
        () => updatedPlan
      );
    },
  });
};

export const useDeletePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      falconClient.delete(`${baseUrl}/plans/${id}`),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: [clientKeys.usePlansIndex],
      });
    },
  });
};
